import React, { useState, Fragment, useEffect, forwardRef } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { BsGrid3X3GapFill } from 'react-icons/bs';

import './BayProductSwitcher.scss';

import BayLogo from '../BayLogo';
import { PRODUCT_LOGO_ALT, PRODUCT_NAME } from '../BayLogo/constants';
import BayTooltip from '../BayTooltip';

import BayProductsModal, { Platform as ProductPlatform } from './components/BayProductsModal';

type Platform = 'financial' | 'social' | 'client' | 'design';

// The menu is rendered with Popper's `fixed` strategy on purpose.
//
// This component mounts inside `SideNav`, whose `.sideNav-layout` sets
// `overflow: hidden` (load-bearing -- without it the footer scrolls out of the
// viewport, see src/SideNav/sidenav.scss). A menu positioned `absolute` inside
// that subtree gets clipped, and when the nav is collapsed the rail is only
// 70px wide against a 262px menu, so it would be clipped badly.
//
// `position: fixed` descendants are not clipped by an ancestor's `overflow`, so
// long as no ancestor establishes a containing block (`transform`, `filter`,
// `perspective`, `will-change`, `contain`). Nothing between here and the body
// does -- `.bay-product-switcher`'s `transition` does not create one. If a
// transform is ever added to the nav chrome, this menu will pin to it and the
// fix is an offset/portal, not a bigger z-index.
//
// This replaced rc-dropdown + rc-menu (removed Aug 2026). Those two brought
// ~270 lines of hand-copied CSS and a second, unmanaged overlay authority; the
// library never imported their stylesheets, so the CSS had been pasted in and
// left to drift. react-bootstrap is the overlay authority for this codebase --
// see CLAUDE.md and docs/css-unification/overlay-and-zindex.md.
// `offset` reproduces the old `left: 1rem !important` -- rc-dropdown wrote
// `left` inline so the stylesheet had to shout over it. Popper writes
// `transform`, so a 16px skid off the toggle's start edge does the same job in
// CSS-free form. The 4px is the gap between the nav header and the menu.
// `offset` is [skidding, distance]. 16 reproduces the old `left: 1rem` inset
// from the switcher's start edge. -5 reproduces the old menu's `margin: -5px`,
// which pulled it up over the switcher's bottom edge -- without it the menu
// hangs 5px lower than it used to. BayProductSwitcher.scss zeroes the Bootstrap
// dropdown spacer so this is the only thing setting the gap.
const POPPER_CONFIG = {
	strategy: 'fixed' as const,
	modifiers: [{ name: 'offset', options: { offset: [16, -5] } }],
};

// react-bootstrap hands the toggle a ref and an onClick; rendering our own means
// no `.btn` classes and no Bootstrap caret to override.
const SwitcherToggle = forwardRef<HTMLDivElement, any>(
	(
		{
			children,
			collapsed,
			platform,
			isMultiPlatform,
			// react-bootstrap hands these down for its own styling; the switcher
			// draws its own chrome, so they are pulled out and dropped rather than
			// spread onto the div (they are not valid DOM attributes).
			bsPrefix: _bsPrefix,
			variant: _variant,
			size: _size,
			// react-bootstrap sets `className="dropdown-toggle"` here, and it MUST be
			// destructured out rather than spread. Two reasons, both bugs that
			// actually shipped:
			//
			// 1. Spreading it landed AFTER our own `className` on the div and
			//    silently replaced it. `bay-product-switcher` carries every bit of
			//    this component's layout -- width, height, flex, background, border
			//    -- so the switcher rendered unstyled, the collapsed rail
			//    (`.collapsed .bay-product-switcher { width: 70px }`) stopped
			//    matching, and `design.scss`'s `.bay-product-switcher { display:
			//    none }` stopped hiding it in the design app.
			// 2. `.dropdown-toggle::after` is Bootstrap's own caret. This component
			//    renders its own `.switcher-chevron`, so the class produced a second,
			//    stray triangle.
			//
			// Nothing else is carried in it -- the aria wiring and onClick are
			// separate props and are still spread below.
			className: _dropdownToggleClassName,
			...toggleProps
		},
		ref,
	) => {
		const label = (
			<span id={`nav-item-${platform}BAY`}>
				{isMultiPlatform ? 'Switch Products' : PRODUCT_NAME[platform]}
			</span>
		);

		return (
			// `toggleProps` carries onClick plus the aria-expanded / aria-haspopup
			// wiring react-bootstrap computes -- spread it, do not cherry-pick
			// onClick, or the toggle stops announcing its state to screen readers.
			<div
				ref={ref}
				className="bay-product-switcher"
				role="button"
				tabIndex={0}
				{...toggleProps}
			>
				{collapsed ? (
					<BayTooltip
						overlay={label}
						placement="bottomLeft"
						trigger="hover"
					>
						<div className="switcher-container multi">{children}</div>
					</BayTooltip>
				) : (
					<div className="switcher-container multi">
						{children}
						<span className="switcher-chevron ms-2" />
					</div>
				)}
			</div>
		);
	},
);

SwitcherToggle.displayName = 'SwitcherToggle';

interface BayProductSwitcherProps {
	collapsed?: boolean;
	isMultiPlatform?: boolean;
	platform?: Platform;
	currentlyUsing?: Platform[];
	isUpsell?: boolean;
	toggled?: boolean;
	featureTransactions?: string | boolean;
	logoLink?: string;
	enableDropdown?: boolean;
}

const BayProductSwitcher = ({
	collapsed = false,
	isMultiPlatform = false,
	platform = 'client',
	currentlyUsing,
	isUpsell = false,
	toggled,
	featureTransactions,
	logoLink,
	enableDropdown = false,
}: BayProductSwitcherProps) => {
	const [showProductModal, setShowProductModal] = useState(false);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		if (!toggled || !collapsed) {
			setIsVisible(false);
		}

		return () => {
			setIsVisible(false);
		};
	}, [collapsed, toggled]);

	const platformArray: Platform[] = ['client', 'financial', 'social']
		.filter((e) =>
			currentlyUsing?.length ? currentlyUsing?.includes(e as Platform) : false,
		)
		.filter((e) => e !== platform) as Platform[];

	const handleSelect = async (itemValue: string) => {
		//FIXME: This is a temporary restriction
		if (itemValue === 'financial') {
			return;
		}

		// The user clicked on browse all
		// pop the upsell modal
		if (itemValue === 'browseAll') {
			setShowProductModal(true);
		} else {
			if (itemValue !== platform) {
				// User clicked on SBAY
				// Use the SSO form
				if (itemValue === 'social') {
					const form = document.getElementById('smcredirect') as HTMLFormElement | null;
					form?.submit();
				} else if (itemValue === 'client') {
					// User  clicked on CBAY
					// If the user is in SBAY
					// use the SSO form ELSE window location
					if (platform === 'social') {
						//TODO; Refactor this not to use jQuery...
						(window as any).jQuery?.useSSOForm();
					} else {
						window.location.href = `http://${
							window.location.host.split('.')[1]
						}.com`;
					}
				} else {
					// User clicked on FBAY
					// redirect the user
					window.location.href = `http://financial.${
						window.location.host.split('.')[1]
							? window.location.host.split('.')[1]
							: window.location.host.split('.')[0]
					}.com`;
				}
			}
		}
	};

	const getLogoComponent = (
		logoLink: string | undefined,
		platform: Platform,
		size = '28px',
	) => {
		const alt = PRODUCT_LOGO_ALT[platform];

		return logoLink && logoLink.length >= 1 ? (
			<a href={logoLink} rel="noopener noreferrer">
				<BayLogo alt={alt} logoStyle="full" platform={platform} size={size} />
			</a>
		) : (
			<BayLogo alt={alt} logoStyle="full" platform={platform} size={size} />
		);
	};

	// Only the multi-product and upsell states have anything to drop down. When
	// neither holds, the toggle is rendered as inert chrome instead.
	const hasMenu = (isMultiPlatform && platformArray.length >= 1) || isUpsell;

	const renderToggleContents = () => (
		<>{!collapsed && getLogoComponent(logoLink, platform)}</>
	);

	const renderInertSwitcher = () => (
		<div className="bay-product-switcher">
			{collapsed ? (
				<BayTooltip
					overlay={
						<span id={`nav-item-${platform}BAY`}>
							{isMultiPlatform ? 'Switch Products' : PRODUCT_NAME[platform]}
						</span>
					}
					placement="bottomLeft"
				>
					<div className="switcher-container single">
						<BsGrid3X3GapFill />
					</div>
				</BayTooltip>
			) : (
				<div className="switcher-container single">
					{getLogoComponent(logoLink, platform)}
				</div>
			)}
		</div>
	);

	return (
		<>
			{enableDropdown ? (
				hasMenu ? (
					<Dropdown
						show={isVisible}
						onSelect={handleSelect}
						onToggle={(nextShow) => setIsVisible(nextShow)}
					>
						<Dropdown.Toggle
							as={SwitcherToggle as any}
							collapsed={collapsed}
							isMultiPlatform={isMultiPlatform}
							platform={platform}
						>
							{renderToggleContents()}
						</Dropdown.Toggle>
						{/*
						 * `renderOnMount` puts the menu in the DOM (hidden) before its
						 * first open. Without it Popper has nothing to measure when the
						 * menu first appears, so it paints at a default position and only
						 * snaps into place on the second open -- the "it flies in from the
						 * right the first time" symptom. Costs one hidden subtree.
						 */}
						<Dropdown.Menu
							renderOnMount
							className="bay-product-switcher-menu"
							popperConfig={POPPER_CONFIG}
						>
							{platformArray.map((item, index) =>
								// financial is rendered nowhere yet -- see the FIXME in
								// handleSelect. Keep the guard and the filter in step.
								item !== 'financial' ? (
									<Fragment key={item}>
										<Dropdown.Item
											eventKey={item}
											className={`menu-item menu-item-${item}`}
										>
											{getLogoComponent(logoLink, item, '21px')}
											<span className="me-2" />
										</Dropdown.Item>
										{index + 1 !== platformArray.length && (
											<Dropdown.Divider className="menu-item-divider" />
										)}
									</Fragment>
								) : null,
							)}

							{isUpsell && (
								<>
									{platformArray.length >= 1 &&
										platformArray.findIndex((e) => e !== 'financial') > -1 && (
											<Dropdown.Divider className="menu-item-divider" />
										)}
									<p className="mb-0 p-3">
										Want to explore our other products? Click below to
										<br /> learn more.
									</p>
									<Dropdown.Divider className="menu-item-divider" />
									<Dropdown.Item
										eventKey="browseAll"
										className="menu-item browse-link"
									>
										Browse All
									</Dropdown.Item>
								</>
							)}
						</Dropdown.Menu>
					</Dropdown>
				) : (
					renderInertSwitcher()
				)
			) : (
				// `single`: a non-interactive logo, so no grey `:hover` band (client only renders this branch).
				<div className="bay-product-switcher">
					<div className="switcher-container single">
						{getLogoComponent(logoLink, platform)}
					</div>
				</div>
			)}
			{showProductModal && (
				<BayProductsModal
					currentlyUsing={currentlyUsing?.filter((p): p is ProductPlatform => p !== 'design')}
					featureTransactions={featureTransactions}
					hideModal={() => setShowProductModal(false)}
					showModal={showProductModal}
				/>
			)}
		</>
	);
};

export default BayProductSwitcher;
